import { DepartmentsManager } from "@/components/DepartmentsManager";
import { useAuth } from "@/context/AuthContext";
import { hasModule } from "@/lib/access";
import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Tag, FolderTree, Building2, ClipboardList } from "lucide-react";

const SECTIONS = [
  { key: "obligation-categories", label: "Obligation categories", endpoint: "/categories", icon: FolderTree , module: "obligations" },
  { key: "obligation-types", label: "Obligation types", endpoint: "/obligation-types", icon: Tag , module: "obligations" },
  { key: "entities", label: "Entities (shared)", endpoint: "/entities", icon: Building2 },
  { key: "task-types", label: "Task types", endpoint: "/task-types", icon: ClipboardList , module: "tasks" },
  { key: "service-categories", label: "Task service categories", endpoint: "/service-categories", icon: FolderTree , module: "tasks" },
];

function Section({ label, endpoint, icon: Icon }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post(endpoint, { name: name.trim() });
      toast.success(`Added "${name}"`);
      setName(""); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-sm font-semibold">{label}</div>
      </div>
      <div className="p-4">
        <div className="flex gap-2 mb-3">
          <Input data-testid={`master-input-${endpoint.slice(1)}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Add new…" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid={`master-add-${endpoint.slice(1)}`} onClick={add} className="bg-blue-700 hover:bg-blue-800 text-white gap-1"><Plus className="w-4 h-4" /> Add</Button>
        </div>
        {loading ? <div className="text-sm text-slate-500 text-center py-4">Loading…</div> : (
          <div className="flex flex-wrap gap-2">
            {items.map((i) => (
              <span key={i.id} data-testid={`master-item-${i.id}`} className={`inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border ${i.is_default ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-white border-slate-300 text-slate-800"}`}>
                {i.name}
                {!i.is_default && <button onClick={() => remove(i.id)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3 h-3" /></button>}
                {i.is_default && <span className="text-[9px] uppercase tracking-wider text-slate-400">default</span>}
              </span>
            ))}
            {items.length === 0 && <div className="text-xs text-slate-500">No items yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MasterSettings() {
  const { user } = useAuth();
  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Configuration</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Master settings</h1>
        <p className="text-sm text-slate-500 mt-1">One place to add or clean up dropdown values used across obligations and tasks.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SECTIONS.filter((x) => !x.module || hasModule(user, x.module)).map(({ key, module, ...rest }) => <Section key={key} {...rest} />)}
      </div>
      {user?.role === "admin" && <div className="mt-4"><DepartmentsManager /></div>}
    </div>
  );
}
